import React, { useMemo, useState } from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  Input,
  Table,
  Progress
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import EcommerceDashboard from "../../ecommerce";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import {
  LAYOUT_MAX_WIDTH_360,
  SUITE_ECOM_PRODUCT_THUMB,
  SUITE_ECOM_RETURN_TIMELINE_DOT,
  SUITE_PROGRESS_HEIGHT_6
} from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import suiteEntities from "../../../../../data/suiteEntities.json";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import {
  AsyncStateBoundary,
  SuitePageShell,
  KpiStatCards
} from "../../_suite/primitives";

function ProductsGalleryPage() {
  const [q, setQ] = useState("");
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.ecommerce.listProducts(),
    []
  );
  const rows = useMemo(() => {
    const list = data || [];
    const t = q.trim().toLowerCase();
    if (!t) return list;
    return list.filter(
      p =>
        String(p.name).toLowerCase().includes(t) ||
        String(p.sku).toLowerCase().includes(t)
    );
  }, [data, q]);

  return (
    <SuitePageShell
      heading={<IntlMessages id="suite.ecom.products" />}
      afterBreadcrumb={
        <p className="text-muted small mb-0">
          Visual catalog — swap thumbnails from your CDN or signed URLs.
        </p>
      }
    >
      <Input
        type="search"
        className="mb-4"
        style={LAYOUT_MAX_WIDTH_360}
        placeholder="Filter SKU or name…"
        value={q}
        onChange={e => setQ(e.target.value)}
      />
      <AsyncStateBoundary loading={loading} error={error} empty={!rows.length}>
        <Row>
          {rows.map(p => (
            <Col md="6" xl="4" key={p.id} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                {p.thumb && (
                  <img
                    src={p.thumb}
                    alt=""
                    className="card-img-top"
                    style={SUITE_ECOM_PRODUCT_THUMB}
                  />
                )}
                <CardBody>
                  <Badge color="light" className="text-dark mb-2">
                    {p.sku}
                  </Badge>
                  <CardTitle tag="h5">{p.name}</CardTitle>
                  <p className="h4 text-primary mb-2">${p.price}</p>
                  <p className="text-muted small mb-3">
                    Stock on hand: <strong>{p.stock}</strong>
                    {p.reorderPoint != null && p.stock <= p.reorderPoint && (
                      <Badge color="warning" className="ms-2">
                        Low stock
                      </Badge>
                    )}
                  </p>
                  {(p.incomingQty || 0) > 0 && (
                    <p className="small text-success mb-3">
                      +{p.incomingQty} units inbound
                    </p>
                  )}
                  <Button
                    tag={Link}
                    color="primary"
                    size="sm"
                    to={p.id}
                    relative="path"
                  >
                    <IntlMessages id="suite.action.open" />
                  </Button>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function ProductDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.ecommerce.getProduct(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.ecom.product-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← <IntlMessages id="suite.action.back" />
      </Button>
      <AsyncStateBoundary
        loading={loading}
        error={error}
        empty={!data}
        emptyMessageId="suite.state.notfound"
      >
        {data && (
          <Row>
            <Col md="5">
              {data.thumb && (
                <img
                  src={data.thumb}
                  alt=""
                  className="img-fluid rounded shadow-sm mb-3"
                />
              )}
            </Col>
            <Col md="7">
              <h2>{data.name}</h2>
              <p className="text-muted">{data.sku}</p>
              <p className="h3 text-primary">${data.price}</p>
              <p>
                Inventory: <Badge color="info">{data.stock} units</Badge>
                {data.reorderPoint != null && (
                  <span className="small text-muted ms-2">
                    Reorder at {data.reorderPoint}
                  </span>
                )}
              </p>
              {(data.incomingQty || 0) > 0 && (
                <p className="small text-success">
                  Purchase order inbound: {data.incomingQty} units
                </p>
              )}
              <Button color="primary" className="me-2">
                Edit listing (wire form)
              </Button>
            </Col>
          </Row>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

type EcomOrder = (typeof suiteEntities.ecomOrders)[number];

function OrdersFulfillmentPage() {
  const { data, loading, error } = useAsyncResource<EcomOrder[]>(
    () => suiteApi.ecommerce.listOrders() as Promise<EcomOrder[]>,
    []
  );
  const byStatus = useMemo(() => {
    const m: {
      processing: EcomOrder[];
      fulfilled: EcomOrder[];
      other: EcomOrder[];
    } = { processing: [], fulfilled: [], other: [] };
    (data || []).forEach((o: EcomOrder) => {
      if (o.status === "processing") m.processing.push(o);
      else if (o.status === "fulfilled") m.fulfilled.push(o);
      else m.other.push(o);
    });
    return m;
  }, [data]);

  const Lane = ({
    title,
    color,
    orders
  }: {
    title: string;
    color: string;
    orders: EcomOrder[];
  }) => (
    <Col md="4" className="mb-4">
      <h6 className={`text-${color} text-uppercase small`}>{title}</h6>
      {orders.map((o: EcomOrder) => (
        <Card key={o.id} className="mb-2 border-0 shadow-sm">
          <CardBody className="py-3">
            <div className="d-flex justify-content-between">
              <strong>{o.id}</strong>
              <Badge color="light" className="text-dark">
                {o.channel}
              </Badge>
            </div>
            <div className="small text-muted">{o.customer}</div>
            <div className="mt-2 font-weight-bold">${o.total}</div>
            <Button
              tag={Link}
              color="link"
              size="sm"
              className="px-0"
              to={o.id}
              relative="path"
            >
              Fulfillment detail →
            </Button>
          </CardBody>
        </Card>
      ))}
      {orders.length === 0 && (
        <p className="text-muted small">No orders in this lane.</p>
      )}
    </Col>
  );

  return (
    <SuitePageShell
      heading={<IntlMessages id="suite.ecom.orders" />}
      afterBreadcrumb={
        <p className="text-muted small mb-0">
          Ops board by fulfillment state — connect to OMS / WMS webhooks.
        </p>
      }
    >
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          <Lane title="Processing" color="warning" orders={byStatus.processing} />
          <Lane title="Fulfilled" color="success" orders={byStatus.fulfilled} />
          <Lane title="Other" color="secondary" orders={byStatus.other} />
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function OrderDetailFulfillmentPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.ecommerce.getOrder(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.ecom.order-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← <IntlMessages id="suite.action.back" />
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <>
            <Card className="mb-4 border-primary border shadow-sm">
              <CardBody className="d-flex flex-wrap justify-content-between align-items-center">
                <div>
                  <h4 className="mb-1">{data.id}</h4>
                  <span className="text-muted">{data.customer}</span>
                </div>
                <div className="text-end">
                  <Badge color="info" className="me-2">
                    {data.status}
                  </Badge>
                  <div className="small">
                    Ship by {data.shipBy} · {data.carrier}
                  </div>
                </div>
              </CardBody>
            </Card>
            {(data.fulfillmentSteps || []).length > 0 && (
              <Card className="mb-4 border-0 bg-light">
                <CardBody>
                  <CardTitle tag="h6">Fulfillment checklist</CardTitle>
                  <ul className="list-unstyled mb-0 small">
                    {data.fulfillmentSteps.map((s, i) => (
                      <li key={i} className="mb-1">
                        {s.done ? "✓" : "○"} {s.step}
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            )}
            <Card className="mb-4">
              <CardBody>
                <CardTitle tag="h5">Line items</CardTitle>
                <Table size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.lineItems || []).map((line, i) => (
                      <tr key={i}>
                        <td>{line.sku}</td>
                        <td>{line.name}</td>
                        <td>{line.qty}</td>
                        <td>${line.price}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function CustomersLtvPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.ecommerce.listCustomers(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.ecom.customers" />}>
      <KpiStatCards
        items={[
          {
            title: "Active customers",
            value: (data || []).length,
            sub: "Demo cohort"
          },
          {
            title: "Avg LTV",
            value: data?.length
              ? `$${Math.round(
                  data.reduce((s, c) => s + (c.ltv || 0), 0) / data.length
                )}`
              : "—"
          }
        ]}
      />
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(c => (
            <Col md="6" key={c.id} className="mb-3">
              <Card className="border-0 shadow-sm">
                <CardBody className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">{c.name}</h5>
                    <div className="small text-muted">{c.email}</div>
                  </div>
                  <div className="text-end">
                    <div className="small text-muted">Orders</div>
                    <div className="h5 mb-0">{c.orders}</div>
                    <div className="small">LTV ${c.ltv}</div>
                    <Button
                      tag={Link}
                      color="outline-primary"
                      size="sm"
                      className="mt-2"
                      to={c.id}
                      relative="path"
                    >
                      Profile
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function CustomerDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.ecommerce.getCustomer(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.ecom.customer-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Card>
            <CardBody>
              <h3>{data.name}</h3>
              <p>{data.email}</p>
              <p>
                Orders: {data.orders} · LTV: ${data.ltv}
              </p>
            </CardBody>
          </Card>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function ReturnsPipelinePage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.ecommerce.listReturns(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.ecom.returns" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <div className="returns-timeline position-relative ps-4">
          {(data || []).map((r, i) => (
            <div key={r.id} className="mb-4 position-relative">
              <span
                className="position-absolute rounded-circle bg-primary"
                style={SUITE_ECOM_RETURN_TIMELINE_DOT}
              />
              <Card className="border-0 shadow-sm">
                <CardBody>
                  <div className="d-flex justify-content-between">
                    <strong>{r.id}</strong>
                    <Badge
                      color={
                        r.status === "approved"
                          ? "success"
                          : r.status === "pending"
                            ? "warning"
                            : "secondary"
                      }
                    >
                      {r.status}
                    </Badge>
                  </div>
                  <div className="small text-muted">Order {r.order}</div>
                  <div>{r.reason}</div>
                  {(r.timeline || []).length > 0 && (
                    <ul className="small text-muted mb-2 ps-3">
                      {r.timeline.map((ev, j) => (
                        <li key={j}>
                          {ev.label} — {ev.at}
                        </li>
                      ))}
                    </ul>
                  )}
                  <Progress
                    value={r.status === "approved" ? 100 : r.status === "pending" ? 45 : 0}
                    className="mt-2"
                    style={SUITE_PROGRESS_HEIGHT_6}
                  />
                </CardBody>
              </Card>
            </div>
          ))}
        </div>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

export function EcommerceSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="ecommerce" />}>
        <Route index element={<EcommerceDashboard />} />
        <Route path="products" element={<ProductsGalleryPage />} />
        <Route path="products/:id" element={<ProductDetailPage />} />
        <Route path="orders" element={<OrdersFulfillmentPage />} />
        <Route path="orders/:id" element={<OrderDetailFulfillmentPage />} />
        <Route path="customers" element={<CustomersLtvPage />} />
        <Route path="customers/:id" element={<CustomerDetailPage />} />
        <Route path="returns" element={<ReturnsPipelinePage />} />
      </Route>
    </Routes>
  );
}
